import { FC, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AuthenticatedHeader } from 'app/features/common/headers/authenticatedHeader'
import { selectAuthState } from 'app/store/selectors'
import { useConsultCache } from 'app/context/ConsultCacheContext'
import { consultQueryRepository } from 'app/api/consult/repository/ConsultQueryRepository'
import {
    subscribeToFormWithConsultId,
    downloadFormPDF,
} from 'app/api/forms/repository/formsRepository'
import { FormDto } from 'app/models/generated'
import { LoadingContainer, Text } from '@tuhi/components'
import { logger } from 'app/logging/Logger'
import { RouteUrl } from '../interface'
import { FormsList } from 'app/features/forms/components/FormsList'
import { SendToAccModal } from 'app/features/forms/modals/SendToAccModal'
import { openModal } from 'app/store/slices/modalSlice'

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr;
`

const LoadingContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`

const EmptyState = styled.div`
    text-align: center;
    padding: 40px 20px;
`

const DownloadingOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
`

const DownloadingModal = styled.div`
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    text-align: center;
`

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const FadeInContent = styled.div`
    animation: ${fadeIn} 0.3s ease-out both;
`

export const FormsRoute: FC = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { currentUser } = useSelector(selectAuthState)
    const { consults, isLoaded } = useConsultCache()
    const [isLoading, setIsLoading] = useState(true)
    const [forms, setForms] = useState<FormDto[]>([])
    const [cacheIssueDetected, setCacheIssueDetected] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)

    // Track active form subscriptions by consult ID
    const formSubscriptionsRef = useRef<
        Map<string, { unsubscribe: () => void }>
    >(new Map())
    // Track forms by consult ID for aggregation
    const formsByConsultRef = useRef<Map<string, FormDto[]>>(new Map())
    // Track how many callbacks received for loading state
    const receivedCallbacksRef = useRef(0)
    // Consults whose forms are watched live (create/update subscriptions)
    const liveConsultIdsRef = useRef<Set<string>>(new Set())

    // ACA-196: every consult id the user owns, fetched independently of the
    // consult cache (which only holds the pages History has scrolled through).
    // null = still loading; on failure fall back to the cache alone.
    const ownerSub = currentUser?.sub
    const [ownerConsultIds, setOwnerConsultIds] = useState<string[] | null>(
        null
    )
    useEffect(() => {
        if (!ownerSub) {
            setOwnerConsultIds([])
            return
        }
        let cancelled = false
        consultQueryRepository
            .listIdsByOwner(ownerSub)
            .then((ids) => {
                if (!cancelled) setOwnerConsultIds(ids)
            })
            .catch((error) => {
                logger.error(
                    'Could not list all consults for Forms, using the cache only',
                    error instanceof Error ? error : new Error(String(error)),
                    { action: 'forms_list_owner_consults_error' }
                )
                if (!cancelled) setOwnerConsultIds([])
            })
        return () => {
            cancelled = true
        }
    }, [ownerSub])

    useEffect(() => {
        if (!isLoaded || ownerConsultIds === null) return

        // Cache consults stay live (new forms appear as they are created);
        // older consults outside the cache get a one-off load, because AppSync
        // caps a connection at 200 subscriptions and each live consult uses 2.
        const cachedIds = new Set(consults.map((c) => c.id))
        // Array.from, not a Set spread: under the ES5 target a spread Set is empty.
        const currentConsultIds = new Set(
            Array.from(cachedIds).concat(ownerConsultIds)
        )
        const previousConsultIds = new Set(
            Array.from(formSubscriptionsRef.current.keys())
        )

        // A consult that was loaded one-off and has since entered the cache
        // (e.g. History scrolled to it) is re-subscribed live.
        const upgradeIds = Array.from(cachedIds).filter(
            (id) =>
                previousConsultIds.has(id) && !liveConsultIdsRef.current.has(id)
        )
        upgradeIds.forEach((id) => {
            formSubscriptionsRef.current.get(id)?.unsubscribe()
            formSubscriptionsRef.current.delete(id)
            previousConsultIds.delete(id)
        })

        // Find new consults (added to cache)
        const addedIds = Array.from(currentConsultIds).filter(
            (id) => !previousConsultIds.has(id)
        )
        // Find removed consults (no longer in cache)
        const removedIds = Array.from(previousConsultIds).filter(
            (id) => !currentConsultIds.has(id)
        )

        // Unsubscribe from removed consults
        removedIds.forEach((id) => {
            const sub = formSubscriptionsRef.current.get(id)
            if (sub) {
                logger.debug('Unsubscribing from forms for removed consult', {
                    action: 'unsubscribe_removed_consult_forms',
                    metadata: { consultId: id },
                })
                sub.unsubscribe()
                formSubscriptionsRef.current.delete(id)
            }
            liveConsultIdsRef.current.delete(id)
            formsByConsultRef.current.delete(id)
        })

        // If consults removed, re-aggregate forms
        if (removedIds.length > 0) {
            const allForms: FormDto[] = []
            formsByConsultRef.current.forEach((f) => allForms.push(...f))
            allForms.sort(
                (a, b) =>
                    new Date(b.sentDate).getTime() -
                    new Date(a.sentDate).getTime()
            )
            setForms(allForms)
        }

        // Handle empty consults case
        if (currentConsultIds.size === 0) {
            logger.debug('No consults found, showing empty state', {
                action: 'no_consults_for_forms',
            })
            setForms([])
            setIsLoading(false)
            return
        }

        // Subscribe to forms for new consults
        addedIds.forEach((consultId) => {
            logger.debug('Subscribing to forms for new consult', {
                action: 'subscribe_consult_forms',
                metadata: { consultId },
            })

            const formSub = subscribeToFormWithConsultId(
                consultId,
                (consultForms) => {
                    receivedCallbacksRef.current++

                    logger.debug('Forms received for consult', {
                        action: 'forms_received_for_consult',
                        metadata: {
                            consultId,
                            formCount: consultForms.length,
                            forms: consultForms.map((f) => ({
                                id: f.id,
                                type: f.type,
                                title: f.title,
                                status: f.status,
                            })),
                        },
                    })

                    if (consultForms.length > 0) {
                        logger.warn(
                            'Cache issue detected: Forms found in local cache but may not exist in database',
                            {
                                action: 'cache_issue_detected',
                                metadata: { consultId },
                            }
                        )
                        setCacheIssueDetected(true)
                    }

                    // Update forms for this consult
                    formsByConsultRef.current.set(consultId, consultForms)

                    // Aggregate all forms
                    const allForms: FormDto[] = []
                    formsByConsultRef.current.forEach((f) =>
                        allForms.push(...f)
                    )

                    // Sort by sent date (newest first)
                    allForms.sort(
                        (a, b) =>
                            new Date(b.sentDate).getTime() -
                            new Date(a.sentDate).getTime()
                    )

                    setForms(allForms)

                    // Stop loading once we've received callbacks from all consults
                    if (
                        receivedCallbacksRef.current >= currentConsultIds.size
                    ) {
                        logger.debug('All form subscriptions received', {
                            action: 'forms_loading_complete',
                            metadata: { totalForms: allForms.length },
                        })
                        setIsLoading(false)
                    }
                },
                { live: cachedIds.has(consultId) }
            )

            if (formSub) {
                formSubscriptionsRef.current.set(consultId, formSub)
                if (cachedIds.has(consultId)) {
                    liveConsultIdsRef.current.add(consultId)
                }
                logger.debug('Form subscription created', {
                    action: 'form_subscription_created',
                    metadata: { consultId },
                })
            } else {
                logger.error(
                    'Failed to create form subscription',
                    new Error('Subscription returned undefined'),
                    {
                        action: 'form_subscription_failed',
                        metadata: { consultId },
                    }
                )
            }
        })

        logger.debug('Form subscriptions adjusted', {
            action: 'form_subscriptions_adjusted',
            metadata: {
                added: addedIds.length,
                removed: removedIds.length,
                total: formSubscriptionsRef.current.size,
            },
        })
    }, [isLoaded, consults, ownerConsultIds])

    // Cleanup all form subscriptions on unmount
    useEffect(() => {
        // Both refs hold Maps that are mutated, never replaced, so these
        // are the same objects the cleanup would read from the ref
        const formSubscriptions = formSubscriptionsRef.current
        const formsByConsult = formsByConsultRef.current
        return () => {
            formSubscriptions.forEach((sub) => sub.unsubscribe())
            formSubscriptions.clear()
            formsByConsult.clear()
            receivedCallbacksRef.current = 0
        }
    }, [])

    const handleFormClick = (formId: string) => {
        navigate(`${RouteUrl.Forms.path}/review/${formId}`, {
            state: { returnTo: '/forms' },
        })
    }

    const handleDownloadPDF = async (formId: string) => {
        logger.debug('Download PDF requested', {
            action: 'download_pdf_start',
            metadata: { formId },
        })

        if (!currentUser?.id) {
            logger.error(
                'Cannot download PDF: user not authenticated',
                new Error('User not authenticated'),
                {
                    action: 'download_pdf_no_user',
                    metadata: { formId },
                }
            )
            alert('Please log in to download PDFs')
            return
        }

        setIsDownloading(true)

        try {
            // Call backend API to get signed S3 URL
            const downloadUrl = await downloadFormPDF(formId, currentUser.id)

            logger.debug('Triggering PDF download', {
                action: 'download_pdf_trigger',
                metadata: { formId },
            })

            // Create a temporary anchor element to trigger download
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = `form-${formId}.pdf`
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            logger.debug('PDF download initiated successfully', {
                action: 'download_pdf_success',
                metadata: { formId },
            })
        } catch (error) {
            const err =
                error instanceof Error ? error : new Error('Unknown error')
            logger.error('Error downloading PDF', err, {
                action: 'download_pdf_error',
                metadata: { formId, error: err.message },
            })
            alert(
                'Failed to download PDF. Please try again or contact support if the issue persists.'
            )
        } finally {
            setIsDownloading(false)
        }
    }

    const handleSendToAcc = (form: FormDto) => {
        const label = [form.type, form.patientName && `for ${form.patientName}`]
            .filter(Boolean)
            .join(' ')
        dispatch(
            openModal({
                children: (
                    <SendToAccModal
                        formId={form.id}
                        formLabel={label || undefined}
                    />
                ),
            })
        )
    }

    const loading = (
        <LoadingContent>
            <LoadingContainer text="Loading forms..." isLoading={true} />
        </LoadingContent>
    )

    const emptyState = (
        <EmptyState>
            <Text size="medium" $weight="regular">
                No forms found
            </Text>
            <Text
                size="small"
                $weight="regular"
                style={{ marginTop: '8px', color: '#666' }}
            >
                Forms will appear here once they are created from consultations
            </Text>
            {cacheIssueDetected && (
                <Text
                    size="small"
                    $weight="regular"
                    style={{ marginTop: '16px', color: '#ff6b6b' }}
                >
                    Cache issue detected. Try clearing your browser cache or
                    contact support.
                </Text>
            )}
        </EmptyState>
    )

    const formsList = (
        <FormsList
            forms={forms}
            onFormClick={handleFormClick}
            onDownloadPDF={handleDownloadPDF}
            onSendToAcc={handleSendToAcc}
        />
    )

    return (
        <Container>
            <AuthenticatedHeader title="Forms" />
            {isLoading ? (
                loading
            ) : (
                <FadeInContent key={forms.length > 0 ? 'forms' : 'empty'}>
                    {forms.length === 0 ? emptyState : formsList}
                </FadeInContent>
            )}
            {isDownloading && (
                <DownloadingOverlay>
                    <DownloadingModal>
                        <LoadingContainer
                            text="Generating PDF..."
                            isLoading={true}
                        />
                    </DownloadingModal>
                </DownloadingOverlay>
            )}
        </Container>
    )
}
